<template>
    <div id="annotate-editor" ref="annotate">
        <div class="tools bg-tertiary">
            <div class="buttons">
                <button
                    v-for="opt in buttonOptions"
                    :key="opt.type"
                    @click="changeMode(opt.type, color)"
                    :class="{
                        active: mode === opt.type
                    }"
                >
                    <i :class="opt.iconClass"></i> {{ opt.label }}
                </button>
                <button @click="undo"><i class="fas fa-undo"></i> Undo</button>
                <button @click="redo"><i class="fas fa-redo"></i> Redo</button>
                <button @click="clearAll">
                    <i class="fas fa-sync"></i> Reset
                </button>
                <button v-if="showRemoveButton" @click="removeSelected">
                    <i class="far fa-trash-alt"></i> Remove
                </button>
            </div>
            <div class="options" v-if="!isSaving">
                <span
                    class="infobtn"
                    v-b-popover.hover.bottom.html="`<b>${name}</b><br/>${file}`"
                    title="<strong>File Information</strong>"
                >
                    <i class="cus-information"></i> Info
                </span>
                <span @click="save"><i class="cus-disk"></i> Save</span>
                <span @click="close"><i class="cus-cancel"></i> Close</span>
            </div>
            <div class="options" v-else>
                <i class="cus-hourglass"></i> Saving in progress
            </div>
        </div>

        <div v-if="isCanvasLoaded" class="editor-wrapper">
            <div class="colors">
                <div
                    v-for="col in colorOptions"
                    :key="col.className"
                    :style="{
                        backgroundColor: col.value,
                        borderColor: color === col.value ? col.value : ''
                    }"
                    @click="changeMode(mode, col.value)"
                >
                    <i v-show="color === col.value" class="fas fa-check"></i>
                </div>
            </div>
            <div class="main-canvas">
                <div class="canvas-wrapper">
                    <Editor
                        :canvasWidth="editorWidth"
                        :canvasHeight="editorHeight"
                        ref="editor"
                        editorId="canvasId"
                    />
                </div>
            </div>
        </div>

        <div v-show="isShowLoader" class="loader-wrapper">
            <div class="loader">
                <loader />
                <p>{{ loaderText }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import Editor from "vue-image-markup";
import Loader from "@/module/commons/loader";
import S3 from "aws-s3-pro";

const DEFAULT_TEXT = {
    fontFamily: "Arial",
    fontSize: 28,
    fontWeight: 100,
    fontStyle: "normal",
    placeholder: "Add Text"
};

const FREE_DRAWING = "freeDrawing";
const SELECT_MODE = "selectMode";
const TEXT = "text";

export default {
    components: {
        Editor,
        Loader
    },
    props: {
        module: {
            type: String,
            validator: value => ["patient", "client"].includes(value)
        },
        /**
         * patient or client name
         */
        name: {
            type: String
        },
        /**
         * current file name
         */
        file: {
            type: String
        },
        imageUrl: {
            type: String
        },
        /**
         * patient or client id
         * based on module
         */
        entityId: {
            type: String
        },
        filename: {
            type: String
        }
    },
    data() {
        return {
            showCloseWarning: true,
            isCanvasLoaded: false,
            editorWidth: 0,
            editorHeight: 0,
            isShowLoader: true,
            loaderText: "Opening...",
            mode: FREE_DRAWING,
            color: "red",
            strokeWidth: 4,
            isSaving: false,
            buttonOptions: [
                {
                    type: FREE_DRAWING,
                    iconClass: "fas fa-paint-brush",
                    label: "Draw"
                },
                {
                    type: "circle",
                    iconClass: "far fa-circle",
                    label: "Circle"
                },
                {
                    type: "rect",
                    iconClass: "far fa-square",
                    label: "Rectangle"
                },
                {
                    type: "arrow",
                    iconClass: "fas fa-long-arrow-alt-right",
                    label: "Arrow"
                },
                {
                    type: TEXT,
                    iconClass: "fas fa-font",
                    label: "Text"
                },
                {
                    type: SELECT_MODE,
                    iconClass: "fas fa-mouse-pointer",
                    label: "Select"
                }
            ],
            colorOptions: [
                {
                    className: "red",
                    value: "red"
                },
                {
                    className: "green",
                    value: "#13b829"
                },
                {
                    className: "blue",
                    value: "#195edf"
                },
                {
                    className: "yellow",
                    value: "#ffcb00"
                },
                {
                    className: "purple",
                    value: "#ac00ff"
                },
                {
                    className: "black",
                    value: "black"
                }
            ],
            currentSelectedObject: null
        };
    },
    computed: {
        showRemoveButton() {
            return this.mode === SELECT_MODE && this.currentSelectedObject;
        }
    },
    beforeDestroy() {
        this.enableBodyScroll();
        this.$store.commit(`${this.module}/setDirty`, false);
    },
    mounted() {
        this.init();
        this.$store.commit(`${this.module}/setDirty`, true);
    },
    methods: {
        removeSelected() {
            if (!this.currentSelectedObject) {
                return;
            }

            let canvas = this.$refs.editor.canvas;
            canvas.remove(canvas.getActiveObject());

            this.currentSelectedObject = null;
        },
        async save() {
            try {
                this.isShowLoader = true;
                this.loaderText = "Saving...";
                this.isSaving = true;

                let mapType = {
                    jpg: "image/jpeg",
                    jpeg: "image/jpeg",
                    png: "image/png",
                    webp: "image/webp",
                    gif: "image/gif"
                };

                let filenameSplitted = this.filename.split(".");
                let ext = filenameSplitted.pop();
                let filenameWithoutExt = filenameSplitted.join(".");

                // let ext = this.filename.split(".").pop();
                let type = mapType[ext] || "image/jpeg";

                // base64
                // (canvas has .toBlob() fn, but Safari tak support)
                let imageData = this.$refs.editor.canvas.toDataURL(type, 1);

                // from base64 to blob
                let blob = dataURItoBlob(imageData); // blob

                // from blob to file
                let fileData = new File([blob], this.filename, {
                    type: type
                });

                let modulePrefix;
                if (this.module === "patient") {
                    modulePrefix = "patient";
                } else if (this.module === "client") {
                    modulePrefix = "actor";
                }

                let prefix = this._s3Path(modulePrefix, this.entityId);
                let s3Path = `${prefix}attachments`;

                let config = {
                    dirName: s3Path,
                    bucketName: process.env.S3_BUCKET,
                    region: process.env.S3_REGION,
                    accessKeyId: process.env.S3_KEY,
                    secretAccessKey: process.env.S3_SECRET,
                    s3Url: process.env.S3_URI
                };

                let S3Client = new S3(config);
                let res = await S3Client.uploadFile(
                    fileData,
                    filenameWithoutExt
                );

                if (res.status === 204) {
                    this._message("Attachment has been updated.");
                    this.proceedClose();
                }
            } catch (e) {
                this.isShowLoader = false;
                this.isSaving = false;
                this._message(e.message, "danger");
            }
        },
        async init() {
            this.preventBodyScroll();

            let main = document.getElementById("annotate-editor");
            let img = this.nocache(this.imageUrl);
            let image = await getMeta(img);

            // 15 = padding (30 = 15x2)
            let workspace = {
                width: main.clientWidth - 30,
                height: main.clientHeight - 30
            };

            // jika salah satu daripada width atau height gambar
            // melebihi saiz workpsace, maka kita scale down kan dia
            if (
                image.width > workspace.width ||
                image.height > workspace.height
            ) {
                let { width, height } = calculateAspectRatioFit(
                    image.width,
                    image.height,
                    workspace.width - 60, // minus color side bar
                    workspace.height - 52 // minus top bar
                );

                this.editorWidth = width;
                this.editorHeight = height;
            } else {
                this.editorWidth = image.width;
                this.editorHeight = image.height;
            }

            window.scrollTo(0, 0);
            this.$refs.annotate.style.top = "0px";
            this.loadCanvas();
        },
        nocache(url) {
            return url + "?_=" + new Date().getTime();
        },
        async loadCanvas() {
            let img = this.nocache(this.imageUrl);
            this.isCanvasLoaded = true;
            await this.$nextTick();
            this.$refs.editor.setBackgroundImage(img);
            this.$refs.editor.set(this.mode, {
                stroke: this.color,
                strokeWidth: this.strokeWidth
            });
            this.isShowLoader = false;

            // event binding
            let self = this;
            this.$refs.editor.canvas.on("selection:created", ({ target }) => {
                self.currentSelectedObject = target;
            });

            this.$refs.editor.canvas.on("selection:cleared", () => {
                self.currentSelectedObject = null;
            });
        },
        changeMode(mode, color) {
            mode = mode || FREE_DRAWING;
            color = color || "red";

            this.mode = mode;
            this.color = color;

            switch (mode) {
                case SELECT_MODE:
                    this.$refs.editor.set(mode);
                    break;

                case TEXT:
                    this.$refs.editor.set(mode, {
                        fontSize: DEFAULT_TEXT.fontSize,
                        fontStyle: DEFAULT_TEXT.fontStyle, // #4051 - `fontStyle` option must be set
                        fill: color
                    });
                    this.currentSelectedObject = null;
                    break;

                default:
                    this.$refs.editor.set(mode, {
                        strokeWidth: this.strokeWidth,
                        stroke: color
                    });
                    this.currentSelectedObject = null;
                    break;
            }
        },
        undo() {
            this.$refs.editor.undo();
            this.mode = null;
            this.color = null;
        },
        redo() {
            this.$refs.editor.redo();
            this.mode = null;
            this.color = null;
        },
        clearAll() {
            let clear = window.confirm(
                "Reset everything? This will clear all the changes made."
            );
            let img = this.nocache(this.imageUrl);

            if (clear) {
                this.$refs.editor.clear();
                this.$refs.editor.setBackgroundImage(img);
            }
        },
        close() {
            let exit = window.confirm(
                "Are you sure you want to close? Any unsaved changes will be lost."
            );

            if (exit) {
                this.proceedClose();
            }
        },
        proceedClose() {
            // basically tells parent to completely destory this editor
            this.$emit("close");
        },
        preventBodyScroll() {
            document.body.style.overflow = "hidden";
            document.body.classList.add("noscroll");
        },
        enableBodyScroll() {
            document.body.style.overflow = "";
            document.body.classList.remove("noscroll");
        }
    }
};

function getMeta(url) {
    return new Promise((resolve, reject) => {
        let img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject();
        img.crossOrigin = "anonymous";
        img.src = url;
    });
}

function calculateAspectRatioFit(srcWidth, srcHeight, maxWidth, maxHeight) {
    var ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
    return {
        width: srcWidth * ratio,
        height: srcHeight * ratio
    };
}

function dataURItoBlob(dataURI, type) {
    var binary = atob(dataURI.split(",")[1]);
    var array = [];
    for (var i = 0; i < binary.length; i++) {
        array.push(binary.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], { type: type });
}
</script>

<style scoped>
#annotate-editor {
    background-color: #ececec;
    position: fixed;
    /* top: 57px; */
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1004;
    opacity: 1;
}
.tools {
    height: 52px;
    display: grid;
    grid-template-columns: 1fr 300px;
    color: #fff;
    align-items: center;
    font-weight: bold;
}
.tools .buttons {
    padding: 0 30px;
    display: flex;
}
.tools .buttons button {
    border: none;
    background-color: transparent;
    color: #fff;
    font-weight: bold;
    padding: 3px 10px;
    border-radius: 5px;
    display: flex;
    align-items: center;
    margin-right: 5px;
}
.tools .buttons button i {
    margin-right: 5px;
    font-size: 1.5em;
}
.tools .buttons button.active {
    background: #55697a;
}
.tools .buttons button:focus {
    outline: unset;
}
.tools .buttons button:hover {
    background: #7792a9;
    border-radius: 3px;
}
.tools .options {
    display: flex;
    justify-content: center;
}
.tools .options span {
    padding: 10px;
    cursor: pointer;
}
.tools .options span:hover:not(.infobtn) {
    background: #7792a9;
    border-radius: 3px;
}
.tools .filename {
    font-weight: normal;
    margin-left: 10px;
}
.info {
    background-color: #8198aa;
    padding: 4px 30px;
    color: #fff;
    font-weight: bold;
    height: 30px;
}
.info span {
    font-weight: normal;
}
.editor-wrapper {
    display: grid;
    grid-template-columns: 60px 1fr;
    height: 100%;
}
.editor-wrapper .colors {
    padding: 15px 0;
    display: flex;
    align-items: center;
    flex-direction: column;
    background-color: #e8e8e8;
    border-right: 1px solid #fff;
}
.editor-wrapper .colors > div {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    margin: 5px 0;
    border: 4px solid #fff;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #fff;
    font-size: 1.4em;
}
.editor-wrapper .colors > div:hover {
    box-shadow: 1px 2px 3px #ababab;
}
.editor-wrapper .colors .red {
    background-color: red;
}
.editor-wrapper .colors .yellow {
    background-color: #ffcb00;
}
.editor-wrapper .colors .purple {
    background-color: #ac00ff;
}
.editor-wrapper .colors .black {
    background-color: black;
}
.editor-wrapper .colors .blue {
    background-color: #195edf;
}
.editor-wrapper .colors .green {
    background-color: #13b829;
}
.editor-wrapper .main-canvas {
    background-color: #ececec;
    display: flex;
    justify-content: center;
    padding: 15px;
}
.loader-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 601;
    display: flex;
    align-items: center;
    justify-content: center;
}
.loader {
    background-color: #fff;
    padding: 10px;
    box-shadow: 0px 2px 10px 3px #ddd;
    border-radius: 5px;
    text-align: center;
}
</style>
